import { addHours, format } from 'date-fns'
import { getCurrentTimeZoneOffset } from '@/shared/lib/getCurrentTimeZoneOffset'
import {
  TaskItem,
  TaskLog,
  AllTaskStats,
  TaskEntityType,
  entityTypeToStatsKeyMap,
  getDefaultStats,
  formatEntityStats,
  TASK_STATUSES,
} from '@/entities/task/model/taskTypes'
import {
  ApiTask,
  ApiTaskLog,
  ApiSubtask,
  LogLevel,
} from '@/entities/task/api/tasksApi'

export const transformApiLogsToTaskLogs = (
  apiLogs: ApiTaskLog[],
  activeTab?: string
): TaskLog[] => {
  if (!apiLogs || !Array.isArray(apiLogs)) {
    return []
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Не указано'
    try {
      const date = new Date(dateString)
      const moscowTime = addHours(date, getCurrentTimeZoneOffset())
      return format(moscowTime, 'dd.MM.yyyy, HH:mm:ss')
    } catch {
      return dateString
    }
  }

  const mapLogLevelToType = (level: LogLevel): TaskLog['type'] => {
    switch (level) {
      case LogLevel.INFO:
        return 'info'
      case LogLevel.WARNING:
        return 'warning'
      case LogLevel.ERROR:
        return 'error'
      default:
        return 'info'
    }
  }

  return apiLogs
    .filter((log) => log && typeof log === 'object')
    .map((log) => {
      const time = formatDate(log.time) || 'Не указано'
      const type = mapLogLevelToType(log.level || LogLevel.INFO)

      let message = log.message || ''

      if (log.code) {
        message = `[Код: ${log.code}] ${message}`
      }

      if (log.details && activeTab === 'all') {
        message = `${message} ${log.details}`
      }

      return {
        time,
        type,
        message: message.trim(),
      }
    })
}

export const transformApiTaskToTaskItem = (
  apiTask: ApiTask
): TaskItem | null => {
  if (!apiTask) return null

  const getTypedStatus = (status: string): TaskItem['status'] => {
    const statusLower = status.toLowerCase()
    switch (statusLower) {
      case 'created':
      case 'pending':
        return TASK_STATUSES.CREATED
      case 'started':
      case 'processing':
      case 'in progress':
      case 'running':
        return TASK_STATUSES.STARTED
      case 'failed':
      case 'error':
        return TASK_STATUSES.FAILED
      case 'successed':
      case 'completed':
      case 'finished':
        return TASK_STATUSES.SUCCESSED
      case 'suspended':
      case 'paused':
        return TASK_STATUSES.SUSPENDED
      case 'deleted':
        return TASK_STATUSES.DELETED
      default:
        return TASK_STATUSES.CREATED
    }
  }

  const typedStatus = getTypedStatus(apiTask.status)

  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''

    try {
      const date = new Date(dateString)
      const moscowTime = addHours(date, getCurrentTimeZoneOffset())
      return format(moscowTime, 'dd.MM.yyyy, HH:mm:ss')
    } catch {
      return dateString
    }
  }

  const formatProgressToPercent = (
    progressValue: number | undefined | null
  ): number => {
    if (progressValue === undefined || progressValue === null) return 0
    return Math.floor(progressValue * 100)
  }

  const getProgressFromSubtasks = (subtasks: ApiSubtask[]) => {
    if (!subtasks || subtasks.length === 0) return 0
    const totalProgress = subtasks.reduce(
      (sum, subtask) => sum + (subtask.progress || 0),
      0
    )
    const averageProgress = totalProgress / subtasks.length
    return formatProgressToPercent(averageProgress)
  }

  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1).replace('.0', '')} M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1).replace('.0', '')} K`
    }
    return num.toString()
  }

  const getStatsFromSubtasks = (subtasks: ApiSubtask[]): AllTaskStats => {
    if (!subtasks || subtasks.length === 0) {
      return getDefaultStats()
    }

    const stats: AllTaskStats = getDefaultStats()

    subtasks.forEach((subtask) => {
      const field = entityTypeToStatsKeyMap[subtask.type as TaskEntityType]

      if (field) {
        const total = subtask.total || 0
        const processed = subtask.processed || 0
        const percent = total > 0 ? Math.floor((processed / total) * 100) : 0

        stats[field].total = total
        stats[field].processed = processed
        stats[field].percent = percent
      }
    })

    return stats
  }

  const rootProgress =
    typedStatus === TASK_STATUSES.CREATED
      ? 0
      : formatProgressToPercent(apiTask.progress)

  const subtasksProgress =
    typedStatus === TASK_STATUSES.CREATED
      ? 0
      : getProgressFromSubtasks(apiTask.subtasks)

  const progress =
    typedStatus === TASK_STATUSES.CREATED
      ? 0
      : rootProgress > 0
        ? rootProgress
        : subtasksProgress

  const stats = getStatsFromSubtasks(apiTask.subtasks)
  const formattedStats = formatEntityStats(stats, formatLargeNumber)

  const errorLogsCount =
    apiTask.logSummary?.errors !== undefined
      ? apiTask.logSummary.errors
      : Array.isArray(apiTask.log)
        ? apiTask.log.filter((log) => log.level === LogLevel.ERROR).length
        : 0

  const hasErrors = errorLogsCount > 0

  const errorMessages: string[] = []
  if (errorLogsCount > 0) {
    errorMessages.push(`Найдено ${errorLogsCount} ошибок в логах`)
  }

  const vehicleIds = apiTask.sourceFilter?.vehicleIds || []
  const isReconfiguration =
    apiTask.subtasks?.some((subtask) => subtask.type === 11) || false

  return {
    id: apiTask.id || 'unknown',
    status: typedStatus,
    createdAt: formatDate(apiTask.created) || 'Не указано',
    startedAt: apiTask.started ? formatDate(apiTask.started) : '',
    completedAt: apiTask.completed ? formatDate(apiTask.completed) : '',
    progress,
    period: apiTask.messagePeriod || 0,
    number: apiTask.number,
    uploadAccount: apiTask.source?.name || apiTask.accountId || '',
    downloadAccount: apiTask.receiver?.name || '',
    downloadLogin: apiTask.receiver?.id || '',
    stats: formattedStats,
    hasErrors,
    system: apiTask.source?.system,
    errorMessage: errorMessages.join(', ') || '',
    vehicleIds,
    isReconfiguration,
  }
}
